import { queryOptions } from '@tanstack/react-query';
import { api, expectBody } from '../../api/client.js';
import type { AgentSnapshot, SettableActivity } from '../../session/agent.machine.js';

/** The agent's own snapshot. The key the `agent:<me>` topic invalidates. */
export const agentSnapshotQueryKey = ['agents', 'me'] as const;

/**
 * `GET /v1/agents/me`, as a query.
 *
 * This is the snapshot the reconnection protocol refetches: whatever the workspace believed about
 * the agent while it was disconnected, this is what replaces it.
 */
export function agentSnapshotQueryOptions() {
  return queryOptions({
    queryKey: agentSnapshotQueryKey,
    queryFn: async (): Promise<AgentSnapshot> => {
      const me = expectBody(await api.GET('/v1/agents/me'));
      return {
        agentId: me.agent.id,
        activity: me.activity,
        reason: me.reason,
        wrapUpSeconds: me.agent.wrapUpSeconds,
        capacities: me.capacities,
        routableChannels: me.routableChannels,
        unreachable: me.unreachable,
        forcedLogout: me.forcedLogout,
      };
    },
  });
}

/**
 * Moves the signed-in agent to another activity.
 *
 * The result is not applied optimistically. The server's event is the transition (state management
 * §2): a change that is refused — not ready without a reason, a transition the agent state machine
 * does not have — must leave the switcher where it was and show why.
 */
export async function setMyActivity(input: {
  readonly activity: SettableActivity;
  readonly reason?: string;
}): Promise<void> {
  await api.POST('/v1/agents/me/activity', {
    body: { activity: input.activity, reason: input.reason },
  });
}

/** Signs the agent in. Not ready unless they ask for available. */
export async function signAgentIn(input: {
  readonly activity: 'available' | 'notReady';
  readonly reason?: string;
}): Promise<void> {
  await api.POST('/v1/agents/me/login', {
    body: { activity: input.activity, reason: input.reason },
  });
}

/** Signs the agent out. Active assignments are not ended by this. */
export async function signAgentOut(): Promise<void> {
  await api.POST('/v1/agents/me/logout', {});
}
